import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import './styles.scss';

export type PublicBlogPost = {
  id: string;
  title: string;
  description?: string | null;
  link?: string | null;
  date?: string | Date | null;
};

function formatPostDate(value?: string | Date | null) {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

function ExpandableDescription({ text }: { text?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (!el || expanded) return;
    setHasOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useEffect(() => {
    const id = requestAnimationFrame(() => measure());
    return () => cancelAnimationFrame(id);
  }, [text, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  if (!text?.trim()) {
    return <p className="home-blog-card__desc home-blog-card__desc--expanded">Sem descrição.</p>;
  }

  return (
    <>
      <p
        ref={contentRef}
        className={
          expanded ? 'home-blog-card__desc home-blog-card__desc--expanded' : 'home-blog-card__desc'
        }
      >
        {text}
      </p>
      {(hasOverflow || expanded) && (
        <button
          type="button"
          className="home-blog-card__toggle"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </>
  );
}

export default function HomeBlogSection() {
  const [posts, setPosts] = useState<PublicBlogPost[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get('/blogs-public/latest');
        if (response.status && response.status.toString()[0] === '2') {
          const body = await response.json();
          setPosts(Array.isArray(body.data) ? body.data : []);
        }
      } catch {
        setPosts([]);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  if (!loaded || posts.length === 0) {
    return null;
  }

  return (
    <section className="home-blog-section" aria-label="Novidades do blog">
      <h2 className="home-blog-section__title">Últimas Notícias</h2>
      {posts.map((post) => (
        <article key={post.id} className="home-blog-card">
          <h3 className="home-blog-card__heading">{post.title}</h3>
          {formatPostDate(post.date) ? (
            <div className="home-blog-card__meta">{formatPostDate(post.date)}</div>
          ) : null}
          <ExpandableDescription text={post.description} />
          {post.link?.trim() ? (
            <div className="home-blog-card__actions">
              <a
                className="home-blog-card__link-btn"
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir link
              </a>
            </div>
          ) : null}
        </article>
      ))}
    </section>
  );
}
